/**
 * Money formatting — native currency + optional converted companion.
 *
 * Replaces the old hardcoded `$`-prefix `fmt$` helpers. Every money figure in
 * the app renders in its TRUE native currency (from the API's `currency`
 * field, sourced from ads_table.currency); cross-currency aggregates and the
 * dual-display subtext use the converted `*_usd` companion the API returns.
 *
 * Rule: show the `≈US$` companion ONLY when the native currency differs from
 * the reporting currency — a USD-native figure needs no "≈US$" echo.
 */

export const REPORTING_DEFAULT = "USD";

/** Format an amount in a given ISO-4217 currency, no decimals (dashboard density). */
export function formatMoney(
  value: number | null | undefined,
  currency: string = REPORTING_DEFAULT,
  opts: { maximumFractionDigits?: number } = {},
): string {
  if (currency === NOT_ONE_CURRENCY_CODE) return "—";
  const n = value ?? 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (currency || REPORTING_DEFAULT).toUpperCase(),
      maximumFractionDigits: opts.maximumFractionDigits ?? 0,
    }).format(n);
  } catch {
    // Unknown/blank currency code → fall back to a plain grouped number with code.
    const num = n.toLocaleString(undefined, {
      maximumFractionDigits: opts.maximumFractionDigits ?? 0,
    });
    return `${(currency || "").toUpperCase()} ${num}`.trim();
  }
}

/** True when a converted companion should be shown beside the native value. */
export function shouldShowConverted(
  currency: string | null | undefined,
  reporting: string = REPORTING_DEFAULT,
): boolean {
  // No single native currency (unknown, or NOT_ONE_CURRENCY_CODE): there is nothing to echo.
  if (isNotOneCurrency(currency)) return false;
  return !!currency && currency.toUpperCase() !== reporting.toUpperCase();
}

/** Build the "≈US$12,345" companion string, or null when redundant. */
export function convertedLabel(
  valueUsd: number | null | undefined,
  currency: string | null | undefined,
  reporting: string = REPORTING_DEFAULT,
): string | null {
  if (!shouldShowConverted(currency, reporting)) return null;
  if (valueUsd === null || valueUsd === undefined) return null;
  return `≈${formatMoney(valueUsd, reporting)}`;
}

/**
 * A budget amount in words with its currency — the frontend twin of the server's
 * `app/services/budget_text.budget_money` (B4-59: no customer surface prints a bare
 * amount). A known currency → "GBP 50.11/day"; unknown → "50.11/day (in the ad
 * account's currency)". Never falls back to an assumed "$".
 */
export const ACCOUNT_CURRENCY = "(in the ad account's currency)";

export function budgetMoney(amount: number, currency?: string | null, opts: { perDay?: boolean } = {}): string {
  const text = amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    + (opts.perDay ? "/day" : "");
  // NOT_ONE_CURRENCY_CODE is no currency code: treated like an unknown currency, never printed.
  const code = isNotOneCurrency(currency) ? "" : (currency || "").trim().toUpperCase();
  return code ? `${code} ${text}` : `${text} ${ACCOUNT_CURRENCY}`;
}

/**
 * VI-1 (live QA r5): a chart's money — axis ticks, tooltips, bar values — in the series' OWN
 * currency, never an assumed "$" (a GBP brand's Metric comparison axes read "$0–$120" over £
 * values). `currency` null means the series has no single currency (a sum across currencies, or
 * not known): the figure is shown bare rather than under a symbol it isn't in. `compact` for axis
 * ticks ("£1.2K").
 */
export function chartMoney(
  value: number | null | undefined,
  currency: string | null | undefined,
  opts: { fractionDigits?: number; compact?: boolean } = {},
): string {
  if (currency === NOT_ONE_CURRENCY_CODE) return "—";
  const n = typeof value === "number" && Number.isFinite(value) ? value : 0;
  const digits = opts.fractionDigits ?? 0;
  const base: Intl.NumberFormatOptions = opts.compact
    ? { notation: "compact", minimumFractionDigits: 0, maximumFractionDigits: 1 }
    : { minimumFractionDigits: digits, maximumFractionDigits: digits };
  const code = (currency || "").trim().toUpperCase();
  if (code) {
    try {
      return new Intl.NumberFormat(undefined, { ...base, style: "currency", currency: code }).format(n);
    } catch {
      /* an unknown code falls through to the bare number with the code */
      return `${code} ${new Intl.NumberFormat(undefined, base).format(n)}`;
    }
  }
  return new Intl.NumberFormat(undefined, base).format(n);
}

/** The currency fields a money-carrying API response returns (app/services/currency_scope.py). */
export interface CurrencyScope {
  currency?: string | null;
  currency_mixed?: boolean | null;
  /** False: several currencies, a row had no FX rate — the money is not one currency's. */
  money_comparable?: boolean | null;
}

/** How a page shows a response's money: in `ccy` (null → bare numbers), reading the FX-converted
 *  `*_usd` companions when `converted` (the scope spans several currencies). */
export interface MoneyBasis {
  ccy: string | null;
  converted: boolean;
}

const warned = new Set<string>();

/** Currency sweep: the basis for a response's money. No "$" fallback — a response with no currency
 *  shows bare numbers, and a development build says which surface is missing one. */
export function moneyBasis(scope: CurrencyScope | null | undefined, where: string): MoneyBasis {
  const ccy = (scope?.currency || "").trim().toUpperCase() || null;
  if (!ccy && scope && import.meta.env.DEV && !warned.has(where)) {
    warned.add(where);
    console.warn(`[money] ${where}: the response carries no currency — figures are shown bare`);
  }
  return { ccy, converted: Boolean(ccy && scope?.currency_mixed) };
}

/** A row's money in the basis: its `<key>_usd` when the scope is converted, else `<key>`. */
export function basisValue(row: object, key: string, basis: MoneyBasis): number {
  const r = row as Record<string, unknown>;
  const v = basis.converted ? r[`${key}_usd`] : r[key];
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

/** A currency's symbol for an input prefix ("£", "€", "$"); "" when the currency isn't known —
 *  the input then carries no prefix rather than an assumed "$". */
export function currencySymbol(currency: string | null | undefined): string {
  if (currency === NOT_ONE_CURRENCY_CODE) return "";
  const code = (currency || "").trim().toUpperCase();
  if (!code) return "";
  try {
    const part = new Intl.NumberFormat(undefined, { style: "currency", currency: code, currencyDisplay: "narrowSymbol" })
      .formatToParts(0).find((p) => p.type === "currency");
    return part?.value ?? code;
  } catch {
    return code;
  }
}

/** False when a response's money can't be put in one currency — its accounts span several and a
 *  row had no FX rate, so the figures are unconverted native sums. Such money is never shown:
 *  money cells render "—" and the section's ⓘ says NOT_ONE_CURRENCY. */
export function moneyComparable(scope: CurrencyScope | null | undefined): boolean {
  if (scope?.money_comparable === false) return false;
  return !(scope?.currency_mixed && !(scope.currency || "").trim());
}

/** The ⓘ tooltip beside a section whose money can't be put in one currency. */
export const NOT_ONE_CURRENCY = "Not available in one currency";

/**
 * The "currency" a page provides (ScopeCurrencyContext / a currency prop) when its money can't be
 * put in one currency — several ad accounts in different currencies and a row without an FX rate,
 * so the figures are native sums (moneyComparable false). chartMoney / formatMoney render "—" for
 * it, so every money cell under that provider shows "—" instead of a bare sum. Never an ISO code.
 */
export const NOT_ONE_CURRENCY_CODE = "NOT_ONE_CURRENCY";

/** The currency to provide for a response's money: its basis currency, or NOT_ONE_CURRENCY_CODE when
 *  the money is not one currency's (lib/money.moneyComparable). */
export function scopeMoneyCurrency(scope: CurrencyScope | null | undefined, where: string): string | null {
  return moneyComparable(scope) ? moneyBasis(scope, where).ccy : NOT_ONE_CURRENCY_CODE;
}

/** True for NOT_ONE_CURRENCY_CODE: the money under this "currency" is not one currency's. */
export function isNotOneCurrency(currency: string | null | undefined): boolean {
  return currency === NOT_ONE_CURRENCY_CODE;
}

/** A ROAS that aggregates money across the scope: "—" when that money isn't one currency's. A
 *  single ad's own ROAS (one ad, one account) stays valid — format that with formatRoas. */
export function scopeRoas<T extends number | null | undefined>(
  roas: T, currency: string | null | undefined, format: (r: T) => string,
): string {
  return currency === NOT_ONE_CURRENCY_CODE ? "—" : format(roas);
}
